import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { translations } from './translations';

const DEFAULT_LANGUAGE = 'fr';

const getInitialLanguage = () => {
  if (typeof window === 'undefined') {
    return DEFAULT_LANGUAGE;
  }

  const stored = window.localStorage.getItem('language');
  if (stored && translations[stored]) {
    return stored;
  }

  const browserLang = (navigator.language || '').slice(0, 2).toLowerCase();
  if (translations[browserLang]) {
    return browserLang;
  }

  return DEFAULT_LANGUAGE;
};

const getValue = (obj, path) => {
  if (!obj || !path) return undefined;
  return path.split('.').reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj);
};

const interpolate = (value, params) => {
  if (!params || typeof value !== 'string') return value;
  return value.replace(/\{(\w+)\}/g, (match, key) => (params[key] !== undefined ? params[key] : match));
};

const LanguageContext = createContext({
  language: DEFAULT_LANGUAGE,
  setLanguage: () => {},
  t: (key) => key,
  isRTL: false,
  locale: 'fr-FR'
});

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(getInitialLanguage);

  const languageData = translations[language] || translations[DEFAULT_LANGUAGE];

  const setLanguage = useCallback((lang) => {
    if (!translations[lang]) return;
    setLanguageState(lang);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('language', lang);
    }
  }, []);

  const t = useCallback((key, params) => {
    const value = getValue(languageData, key);
    const fallback = getValue(translations[DEFAULT_LANGUAGE], key);
    return interpolate(value !== undefined ? value : fallback !== undefined ? fallback : key, params);
  }, [languageData]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.lang = languageData.meta.locale || language;
    document.documentElement.dir = languageData.meta.dir || 'ltr';
  }, [language, languageData.meta.dir, languageData.meta.locale]);

  const value = useMemo(() => ({
    language,
    setLanguage,
    t,
    isRTL: languageData.meta.dir === 'rtl',
    locale: languageData.meta.locale || 'fr-FR',
    languageMeta: languageData.meta
  }), [language, languageData, setLanguage, t]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);
